import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/ui';
import QuestionCard from '../components/QuestionCard';
import { useQuestions, useTopics, useSubtopics } from '../hooks/useQuestions';
import { exportQuestionsPdf } from '../utils/pdfExport';
import { isMultiAnswer, isSlotBased, formatSlotAnswers } from '../utils/questionHelpers';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { fetchSessionResume, updateCursor, submitAnswer, completeSession } from '../api/sessions';
import { fetchDailyUsage } from '../api/questions';
import { fetchFlaggedIds, flagQuestion, unflagQuestion } from '../api/flagged';
import { useAuthStore } from '../stores/authStore';
import { useCertStore } from '../stores/certStore';
import { useIsPremium } from '../stores/premiumStore';
import { useCanAccessCert } from '../stores/entitlementStore';
import type { Question } from '../types';
import { InlineAd } from '../components/AdUnit';
import DailyLimitBanner from '../components/DailyLimitBanner';

import { generateSeed, loadCursor, saveCursor, clearCursor } from '../hooks/useStudyCursor';
import type { StudyCursor } from '../hooks/useStudyCursor';
import { Button, Card, Modal, TextField } from '../components/ui';

// ── InfoTooltip ────────────────────────────────────────────────────────────

function InfoTooltip({ text }: { text: string }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        aria-label={t('study.session.moreInfo')}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
      </button>
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-xl pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

// ── PDF Export Modal ───────────────────────────────────────────────────────

interface PdfModalProps {
  open: boolean;
  visibleCount: number;
  batchCount: number;
  onExport: (count: 'visible' | 'batch' | number) => void;
  onClose: () => void;
}

function PdfExportModal({ open, visibleCount, batchCount, onExport, onClose }: PdfModalProps) {
  const { t } = useTranslation();
  const [customCount, setCustomCount] = useState('');
  return (
    <Modal open={open} onClose={onClose} ariaLabelledBy="pdf-export-title" size="sm">
      <Modal.Header id="pdf-export-title">{t('study.session.pdfExportTitle')}</Modal.Header>
      <Modal.Body>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          {t('study.session.pdfExportSubtitle')}
        </p>
        <div className="space-y-2">
          {[
            { label: t('study.session.pdfCurrentView', { count: visibleCount }), value: 'visible' as const },
            { label: t('study.session.pdfEntireBatch', { count: batchCount }), value: 'batch' as const },
          ].map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => { onExport(value); onClose(); }}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-card-dark/60 dark:text-slate-200 dark:hover:bg-white/10"
            >
              {label}
            </button>
          ))}
          <div className="flex gap-2 pt-1">
            <TextField
              type="number"
              min={1}
              max={batchCount}
              placeholder={t('study.session.pdfCustomPlaceholder', { max: batchCount })}
              value={customCount}
              onChange={(e) => setCustomCount(e.target.value)}
              wrapperClassName="flex-1"
            />
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                const n = Math.min(Math.max(1, Number(customCount)), batchCount);
                if (n) { onExport(n); onClose(); }
              }}
              disabled={!customCount}
            >
              {t('study.session.pdfExport')}
            </Button>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-center">
        <button
          type="button"
          onClick={onClose}
          className="w-full text-center text-xs text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          {t('study.session.pdfCancel')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export function StudyModeContent({
  searchParams,
  setSearchParams,
  sessionId,
}: {
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  sessionId: number | null;
}) {
  const { t } = useTranslation();
  useDocumentTitle(t('study.session.documentTitle'));
  const certCode = useCertStore((s) => s.certCode);
  const certName = useCertStore((s) => s.certName);
  const user = useAuthStore((s) => s.user);
  const isPremium = useIsPremium();
  const canAccessCert = useCanAccessCert(certCode);
  const queryClient = useQueryClient();

  // User has premium-level access if they have global premium OR per-cert access
  const hasCertAccess = isPremium || canAccessCert;

  // ── Flag "for review" (tricky/interesting) ────────────────────────────
  const { data: flaggedIdsData } = useQuery({
    queryKey: ['flagged-ids'],
    queryFn: fetchFlaggedIds,
    staleTime: 60_000,
  });
  const [flagged, setFlagged] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (flaggedIdsData) setFlagged(new Set(flaggedIdsData));
  }, [flaggedIdsData]);
  const handleToggleFlag = useCallback((id: number) => {
    setFlagged((prev) => {
      const next = new Set(prev);
      const turningOn = !next.has(id);
      if (turningOn) next.add(id);
      else next.delete(id);
      (turningOn ? flagQuestion(id) : unflagQuestion(id))
        .then(() => queryClient.invalidateQueries({ queryKey: ['flagged'] }))
        .catch(() => {
          // Revert optimistic toggle on failure.
          setFlagged((p) => {
            const r = new Set(p);
            if (turningOn) r.delete(id);
            else r.add(id);
            return r;
          });
        });
      return next;
    });
  }, [queryClient]);

  // ── Daily usage tracking (free users) ─────────────────────────────────
  const { data: dailyUsage } = useQuery<{ limit: number | null; used: number; remaining: number | null; is_limited: boolean }>({
    queryKey: ['daily-usage'],
    queryFn: fetchDailyUsage,
    enabled: !!user && !hasCertAccess,
    refetchInterval: 60000,
  });
  const isFreeUser = !!user && !hasCertAccess;

  const topicFilter = searchParams.get('topic') ?? undefined;
  const subtopicFilter = searchParams.get('subtopic') ?? undefined;
  const difficultyFilter = searchParams.get('difficulty') ?? undefined;
  const pageParam = Number(searchParams.get('page') ?? '0');

  const [pageSize, setPageSize] = useState(() =>
    Number(localStorage.getItem(`cloudmaster_page_size_${certCode}`) ?? '25'),
  );
  const [viewCount, setViewCount] = useState(() => {
    const storedView = Number(localStorage.getItem(`cloudmaster_view_count_${certCode}`) ?? '1');
    const storedPageSize = Number(localStorage.getItem(`cloudmaster_page_size_${certCode}`) ?? '25');
    // A screen can never show more questions than a batch fetches.
    return Math.min(Math.max(1, storedView || 1), storedPageSize || 25);
  });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Seed for deterministic shuffle — generated once per filter combination, persisted
  const [sessionSeed, setSessionSeed] = useState<string>(() => {
    const saved = loadCursor(
      certCode,
      new URLSearchParams(window.location.search).get('topic') ?? undefined,
      new URLSearchParams(window.location.search).get('difficulty') ?? undefined,
    );
    return saved?.seed ?? generateSeed();
  });

  // Track how many questions revealed in this session
  const [sessionRevealed, setSessionRevealed] = useState(0);
  // Track reveals since last dailyUsage API refetch (for live progress).
  // We store a running total plus the server baseline; the delta is derived.
  const [revealsSinceRefetch, setRevealsSinceRefetch] = useState(0);
  const lastKnownServerUsed = useRef(dailyUsage?.used ?? 0);

  // When dailyUsage refetches with a new server count, reset the local delta.
  // setState here is intentional: we sync local counter with the server baseline.
  useEffect(() => {
    if (dailyUsage && dailyUsage.used !== lastKnownServerUsed.current) {
      lastKnownServerUsed.current = dailyUsage.used;
      setRevealsSinceRefetch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyUsage?.used]);
  // Live count: server value + local reveals since last refetch
  const freeUsed = Math.min((dailyUsage?.used ?? 0) + revealsSinceRefetch, 20);
  const isLimitReached = isFreeUser && freeUsed >= 20;
  // Resume banner
  const [resumeBanner, setResumeBanner] = useState<StudyCursor | null>(null);
  const didRestoreCursor = useRef(false);

  // Scroll the questions column back to its head (below the sticky topbar +
  // study header) after navigating, so a new batch starts at question 1 rather
  // than leaving the viewport parked on the previous batch's last question.
  const questionsColumnRef = useRef<HTMLDivElement>(null);
  const scrollToQuestionsTop = useCallback(() => {
    requestAnimationFrame(() => {
      questionsColumnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Per-question answer state: questionId -> { selected, slotSelections, revealed, hintRevealed, correct_answer, explanation }
  const [answerState, setAnswerState] = useState<
    Record<number, {
      selected: string | string[] | null;
      slotSelections?: Record<number, string>;
      revealed: boolean;
      hintRevealed: boolean;
      correct_answer?: string | null;
      explanation?: string | null;
    }>
  >({});

  /* ── Server session resume ───────────────────────────────────────────────── */
  const { data: resumeData } = useQuery({
    queryKey: ['session-resume', sessionId],
    queryFn: () => fetchSessionResume(sessionId!),
    enabled: sessionId !== null,
    staleTime: Infinity,
  });

  const sessionApplied = useRef(false);
  useEffect(() => {
    if (!resumeData || sessionApplied.current) return;
    sessionApplied.current = true;
    // Apply server-side session state via queueMicrotask to avoid
    // synchronous setState within an effect body.
    queueMicrotask(() => {
      if (resumeData.seed) setSessionSeed(resumeData.seed);
      if (resumeData.page_size) setPageSize(resumeData.page_size);
      // Restore cursor position
      if (resumeData.cursor_page > 0 || resumeData.cursor_idx > 0) {
        setCurrentIdx(resumeData.cursor_idx);
      }
      // Restore answered questions
      if (resumeData.answered_questions.length > 0) {
        const restored: Record<number, { selected: string | string[] | null; revealed: boolean; hintRevealed: boolean }> = {};
        for (const aq of resumeData.answered_questions) {
          restored[aq.question_id] = { selected: aq.given_answer, revealed: true, hintRevealed: false };
        }
        setAnswerState(restored);
        setSessionRevealed(resumeData.answered_questions.length);
      }
    });
    // Handle URL update for cursor restoration
    if (resumeData.cursor_page > 0 || resumeData.cursor_idx > 0) {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(resumeData.cursor_page));
      setSearchParams(params);
    }
  }, [resumeData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced server cursor save (2s)
  const cursorSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveCursorToServer = useCallback(
    (page: number, idx: number, globalIdx: number, total: number) => {
      if (!sessionId) return;
      if (cursorSaveTimer.current) clearTimeout(cursorSaveTimer.current);
      cursorSaveTimer.current = setTimeout(() => {
        updateCursor(sessionId, {
          cursor_page: page,
          cursor_idx: idx,
          cursor_global: globalIdx,
          total_questions: total,
        }).catch(() => {}); // silent fail
      }, 2000);
    },
    [sessionId],
  );

  // Use session filters if available, else URL params
  const effectiveTopic = sessionId && resumeData?.topic_filter ? resumeData.topic_filter : topicFilter;
  const effectiveDifficulty = sessionId && resumeData?.difficulty_filter ? resumeData.difficulty_filter : difficultyFilter;
  // Subtopic lives only in the unified filter_spec (no legacy column).
  const effectiveSubtopic =
    sessionId && resumeData?.filter_spec?.subtopics?.length
      ? resumeData.filter_spec.subtopics.join(',')
      : subtopicFilter;
  // question_type lives ONLY in the unified filter_spec (no legacy column).
  // Pre-052 sessions have no filter_spec → undefined → no type filter (correct).
  const effectiveQuestionType =
    sessionId && resumeData?.filter_spec?.questionTypes?.length
      ? resumeData.filter_spec.questionTypes.join(',')
      : undefined;

  // When a sessionId is present, wait for resume data before fetching questions.
  // This prevents a premature fetch with no filters (which loads ALL questions).
  // When there is no sessionId (direct /study access), fetch immediately.
  const resumeReady = sessionId === null || resumeData !== undefined;

  /* ── Data fetching ──────────────────────────────────────────────────────── */
  const { data: topics, isLoading: topicsLoading } = useTopics();
  // Subtopics for the currently-selected domain (narrow-drill selector).
  const { data: subtopics } = useSubtopics(effectiveTopic, !!effectiveTopic && !isFreeUser);
  // Cap offset and limit when session has a question count limit
  const rawSessionLimit = sessionId !== null && resumeData?.total_questions ? resumeData.total_questions : 0;
  const fetchOffset = pageParam * pageSize;
  const fetchLimit = rawSessionLimit > 0
    ? Math.max(0, Math.min(pageSize, rawSessionLimit - fetchOffset))
    : pageSize;
  const {
    data: questionData,
    isLoading: questionsLoading,
    error: questionsError,
  } = useQuestions(
    {
      topic: effectiveTopic,
      subtopic: effectiveSubtopic,
      difficulty: effectiveDifficulty,
      question_type: effectiveQuestionType,
      offset: fetchOffset,
      limit: fetchLimit,
      seed: sessionSeed,
      certification: certCode,
    },
    resumeReady,
  );

  const questions = questionData?.items ?? [];
  // When a session has a configured question count, cap the total to that limit
  const totalQuestions = rawSessionLimit > 0
    ? Math.min(questionData?.total ?? 0, rawSessionLimit)
    : (questionData?.total ?? 0);

  // Questions-per-screen choices, capped at the current batch size (can't show
  // more per screen than a batch fetches). Goes up to 50.
  const viewCountOptions = [1, 2, 3, 4, 5, 10, 20, 30, 50].filter((n) => n <= pageSize);

  // Get visible questions slice
  const visibleQuestions = questions.slice(currentIdx, currentIdx + viewCount);

  // Show resume banner once when data loads and cursor exists.
  useEffect(() => {
    if (didRestoreCursor.current || questionsLoading || !totalQuestions) return;
    didRestoreCursor.current = true;
    const saved = loadCursor(certCode, topicFilter, difficultyFilter);
    if (saved && saved.total > 0 && saved.globalIdx > 1) {
      queueMicrotask(() => setResumeBanner(saved));
    }
  }, [questionsLoading, totalQuestions, topicFilter, difficultyFilter, certCode]);

  // Auto-save cursor on every navigation
  const globalIdx = pageParam * pageSize + currentIdx + 1;
  const globalEnd = Math.min(globalIdx + viewCount - 1, totalQuestions);

  useEffect(() => {
    if (!totalQuestions || questionsLoading) return;
    saveCursor(certCode, topicFilter, difficultyFilter, {
      page: pageParam,
      idx: currentIdx,
      globalIdx,
      total: totalQuestions,
      seed: sessionSeed,
      savedAt: Date.now(),
    });
    // Also save cursor to server if using a session
    saveCursorToServer(pageParam, currentIdx, globalIdx, totalQuestions);
  }, [pageParam, currentIdx, totalQuestions, questionsLoading, topicFilter, difficultyFilter, globalIdx, sessionSeed, saveCursorToServer, certCode]);

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === undefined) params.delete(k);
        else params.set(k, v);
      });
      params.set('page', '0');
      setSearchParams(params);
      setCurrentIdx(0);
      setAnswerState({});
      setSessionRevealed(0);
      // New filter = check if a saved cursor exists, otherwise fresh seed
      const newTopic = updates.topic !== undefined ? updates.topic : topicFilter;
      const newDiff = updates.difficulty !== undefined ? updates.difficulty : difficultyFilter;
      const saved = loadCursor(certCode, newTopic, newDiff);
      setSessionSeed(saved?.seed ?? generateSeed());
      didRestoreCursor.current = false;
    },
    [searchParams, setSearchParams, topicFilter, difficultyFilter, certCode],
  );

  const handleSelectTopic = useCallback(
    // Changing the domain clears any active subtopic (subtopics belong to a domain).
    (topic: string | undefined) => updateParams({ topic, subtopic: undefined }),
    [updateParams],
  );

  const handleSelectSubtopic = useCallback(
    (subtopic: string | undefined) => updateParams({ subtopic }),
    [updateParams],
  );

  const handleSelectDifficulty = useCallback(
    (diff: string | undefined) => updateParams({ difficulty: diff }),
    [updateParams],
  );

  const handleSelectOption = useCallback(
    (questionId: number, questionType: string, letter: string) => {
      setAnswerState((prev) => {
        const cur = prev[questionId] ?? { selected: null, revealed: false, hintRevealed: false };
        if (cur.revealed) return prev;
        if (isMultiAnswer(questionType)) {
          const arr = Array.isArray(cur.selected) ? cur.selected : [];
          const newSel = arr.includes(letter) ? arr.filter((l) => l !== letter) : [...arr, letter];
          return { ...prev, [questionId]: { ...cur, selected: newSel } };
        }
        return { ...prev, [questionId]: { ...cur, selected: letter } };
      });
    },
    [],
  );

  const handleSlotSelect = useCallback(
    (questionId: number, slotId: number, value: string) => {
      setAnswerState((prev) => {
        const cur = prev[questionId] ?? { selected: null, revealed: false, hintRevealed: false };
        if (cur.revealed) return prev;
        const slots = { ...(cur.slotSelections ?? {}), [slotId]: value };
        return { ...prev, [questionId]: { ...cur, slotSelections: slots } };
      });
    },
    [],
  );

  const handleHint = useCallback(
    (questionId: number) => {
      setAnswerState((prev) => ({
        ...prev,
        [questionId]: {
          ...(prev[questionId] ?? { selected: null, revealed: false, hintRevealed: false }),
          hintRevealed: true,
        },
      }));
    },
    [],
  );

  const handleReveal = useCallback(
    (questionId: number, question?: Question) => {
      setAnswerState((prev) => {
        if (prev[questionId]?.revealed) return prev;
        setSessionRevealed((n) => n + 1);
        setRevealsSinceRefetch((n) => n + 1);
        const state = prev[questionId];
        let givenAnswer: string;
        if (question && isSlotBased(question) && state?.slotSelections) {
          givenAnswer = formatSlotAnswers(state.slotSelections, question.answer_slots!);
        } else {
          const selected = state?.selected;
          givenAnswer = Array.isArray(selected) ? selected.sort().join('') : (selected ?? '');
        }
        // Submit answer to server if session is active
        if (sessionId && givenAnswer) {
          submitAnswer(sessionId, { question_id: questionId, given_answer: givenAnswer })
            .then((resp) => {
              // Store correct_answer and explanation from server response
              if (resp.correct_answer || resp.explanation) {
                setAnswerState((p) => ({
                  ...p,
                  [questionId]: {
                    ...p[questionId],
                    correct_answer: resp.correct_answer,
                    explanation: resp.explanation,
                  },
                }));
              }
              // Refetch daily usage so the UI updates immediately
              queryClient.invalidateQueries({ queryKey: ['daily-usage'] });
            })
            .catch(() => {
              // If 403 DAILY_LIMIT_REACHED, refetch to trigger blur
              queryClient.invalidateQueries({ queryKey: ['daily-usage'] });
            });
        }
        return { ...prev, [questionId]: { ...prev[questionId], revealed: true } };
      });
    },
    [sessionId, queryClient],
  );

  const handleResume = useCallback(() => {
    if (!resumeBanner) return;
    // Restore the saved seed so order is identical
    setSessionSeed(resumeBanner.seed);
    const params = new URLSearchParams(searchParams);
    params.set('page', String(resumeBanner.page));
    setSearchParams(params);
    setCurrentIdx(resumeBanner.idx);
    setAnswerState({});
    setResumeBanner(null);
  }, [resumeBanner, searchParams, setSearchParams]);

  const handleResetCursor = useCallback(() => {
    clearCursor(certCode, topicFilter, difficultyFilter);
    setResumeBanner(null);
    // Fresh seed = new random order
    const newSeed = generateSeed();
    setSessionSeed(newSeed);
    const params = new URLSearchParams(searchParams);
    params.set('page', '0');
    setSearchParams(params);
    setCurrentIdx(0);
    setAnswerState({});
    setSessionRevealed(0);
  }, [certCode, topicFilter, difficultyFilter, searchParams, setSearchParams]);

  const handleNext = useCallback(() => {
    const nextIdx = currentIdx + viewCount;
    if (nextIdx < questions.length) {
      setCurrentIdx(nextIdx);
    } else if (questionData && (pageParam + 1) * pageSize < totalQuestions) {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(pageParam + 1));
      setSearchParams(params);
      setCurrentIdx(0);
    }
    setAnswerState({});
    scrollToQuestionsTop();
  }, [currentIdx, viewCount, questions.length, questionData, pageParam, pageSize, totalQuestions, searchParams, setSearchParams, scrollToQuestionsTop]);

  const handlePrev = useCallback(() => {
    if (currentIdx >= viewCount) {
      setCurrentIdx(currentIdx - viewCount);
    } else if (pageParam > 0) {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(pageParam - 1));
      setSearchParams(params);
      setCurrentIdx(Math.max(0, pageSize - viewCount));
    }
    setAnswerState({});
    scrollToQuestionsTop();
  }, [currentIdx, viewCount, pageParam, pageSize, searchParams, setSearchParams, scrollToQuestionsTop]);

  /* ── Difficulty pills ──────────────────────────────────────────────────── */
  const difficultyOptions = [
    { value: undefined, label: t('study.session.difficultyAll'), icon: '' },
    { value: 'easy', label: t('study.session.difficultyEasy'), icon: '🟢' },
    { value: 'medium', label: t('study.session.difficultyMedium'), icon: '🟡' },
    { value: 'hard', label: t('study.session.difficultyHard'), icon: '🔴' },
  ] as const;

  /* ── Sidebar ────────────────────────────────────────────────────────────── */
  const freePct = Math.min((freeUsed / 20) * 100, 100);
  const freeRemaining = Math.max(0, 20 - freeUsed);

  const sidebarContent = (
    <aside className="w-full shrink-0 space-y-4 lg:sticky lg:top-16 lg:w-64 lg:self-start lg:max-h-[calc(100vh-4.5rem)] lg:overflow-y-auto lg:pb-4">
      {/* Free-user usage card with live progress */}
      {isFreeUser && (
        <Card className="p-4 ring-1 ring-amber-500/30">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-900 dark:text-white">{t('study.session.freePlan')}</p>
            <span className={`text-xs font-bold tabular-nums ${freeRemaining === 0 ? 'text-red-600 dark:text-red-400' : freeRemaining <= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`}>
              {freeUsed}/20
            </span>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${freeRemaining === 0 ? 'bg-red-500' : freeRemaining <= 5 ? 'bg-amber-500' : 'bg-blue-600'}`}
              style={{ width: `${freePct}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            {freeRemaining === 0
              ? t('study.session.limitReached')
              : t('study.session.questionsRemainingToday', { count: freeRemaining })}
          </p>
          <Link to="/pricing" className="mt-2 inline-block text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400">
            {t('study.session.upgradeToPremium')}
          </Link>
        </Card>
      )}

      {/* Topics — visible but disabled for free users */}
      <Card className={`p-4 ${isFreeUser ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t('study.session.topics')}
          </h2>
          {isFreeUser && (
            <span className="rounded-full bg-slate-200 dark:bg-white/10 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-500 dark:text-slate-400">{t('study.session.premiumBadge')}</span>
          )}
        </div>
        {topicsLoading && <p className="text-sm text-slate-500 dark:text-slate-400">{t('study.session.loadingTopics')}</p>}
        <ul className="space-y-1">
          <li>
            <button
              type="button"
              onClick={() => handleSelectTopic(undefined)}
              disabled={isFreeUser}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                !topicFilter
                  ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5'
              }`}
            >
              {t('study.session.allTopics')}
              <span className="ml-auto float-right tabular-nums text-slate-500 dark:text-slate-400">{totalQuestions}</span>
            </button>
          </li>
          {topics?.map((topic) => (
            <li key={topic.topic}>
              <button
                type="button"
                onClick={() => handleSelectTopic(topic.topic)}
                disabled={isFreeUser}
                className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  topicFilter === topic.topic
                    ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5'
                }`}
              >
                {topic.topic}
                <span className="ml-auto float-right tabular-nums text-slate-500 dark:text-slate-400">{topic.count}</span>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {/* Subtopics — narrow-drill selector, shown once a domain is picked */}
      {effectiveTopic && (subtopics?.some((s) => s.count > 0) ?? false) && (
        <Card className={`p-4 ${isFreeUser ? 'opacity-50 pointer-events-none' : ''}`}>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t('study.session.subtopics')}
          </h2>
          <ul className="space-y-1">
            <li>
              <button
                type="button"
                onClick={() => handleSelectSubtopic(undefined)}
                disabled={isFreeUser}
                className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  !subtopicFilter
                    ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5'
                }`}
              >
                {t('study.session.allSubtopics')}
              </button>
            </li>
            {subtopics?.filter((s) => s.count > 0).map((s) => (
              <li key={s.name}>
                <button
                  type="button"
                  onClick={() => handleSelectSubtopic(s.name)}
                  disabled={isFreeUser}
                  title={s.name}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    subtopicFilter === s.name
                      ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="truncate">{s.name}</span>
                  <span className="ml-auto shrink-0 tabular-nums text-slate-500 dark:text-slate-400">{s.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Difficulty filter — visible but disabled for free users */}
      <Card className={`p-4 ${isFreeUser ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t('study.session.difficulty')}
          </h2>
          {isFreeUser && (
            <span className="rounded-full bg-slate-200 dark:bg-white/10 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-500 dark:text-slate-400">{t('study.session.premiumBadge')}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {difficultyOptions.map((d) => {
            const active = difficultyFilter === d.value || (!difficultyFilter && !d.value);
            return (
              <button
                key={d.label}
                type="button"
                onClick={() => handleSelectDifficulty(d.value)}
                disabled={isFreeUser}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-blue-600 text-white shadow-[0_4px_12px_-4px_rgba(37,99,235,0.5)]'
                    : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10'
                }`}
              >
                {d.icon} {d.label}
              </button>
            );
          })}
        </div>
      </Card>
    </aside>
  );

  return (
    <AppShell maxWidth="7xl" mainClassName="!px-0 !py-0">
      <PdfExportModal
        open={showPdfModal}
        visibleCount={visibleQuestions.length}
        batchCount={questions.length}
        onClose={() => setShowPdfModal(false)}
        onExport={(count) => {
            const sourceQ = count === 'visible' ? visibleQuestions : count === 'batch' ? questions : questions.slice(0, count);
            const startNum = count === 'visible' ? globalIdx : pageParam * pageSize + 1;
            const pdfQ = sourceQ.map((q, i) => ({
              number: startNum + i,
              text: q.question_text,
              options: q.options.map((o) => ({ letter: o.letter, text: o.option_text })),
              correct_answer: q.correct_answer,
              explanation: q.explanation,
              topic: q.topic,
              difficulty: q.difficulty,
              images: q.images,
            }));
            exportQuestionsPdf(pdfQ).catch(console.error);
          }}
        />

      <div className="mx-auto flex max-w-7xl flex-1 flex-col gap-6 px-4 py-8 sm:px-6 lg:flex-row lg:px-8">
        {sidebarContent}

        <div className="flex-1 min-w-0 scroll-mt-16" ref={questionsColumnRef}>

          {/* ── Resume banner ─────────────────────────────────────────── */}
          {resumeBanner && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm dark:border-blue-500/30 dark:bg-blue-500/10">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                </svg>
                <span>{t('study.session.resumeBannerPrefix')} <strong>{t('study.session.resumeBannerMiddle', { globalIdx: resumeBanner.globalIdx })}</strong> {t('study.session.resumeBannerSuffix', { total: resumeBanner.total })}</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="primary" size="sm" onClick={handleResume}>
                  {t('study.session.resume')} →
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={handleResetCursor}>
                  {t('study.session.startOver')}
                </Button>
              </div>
            </div>
          )}

          {/* ── Study header — sticky on desktop so only questions scroll ─ */}
          <div className="bg-canvas dark:bg-canvas-dark lg:sticky lg:top-16 lg:z-20 lg:border-b lg:border-slate-200/70 lg:pt-2 lg:pb-3 dark:lg:border-white/10">
          {/* ── Controls bar ──────────────────────────────────────────── */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('study.session.title')}
              <span className="ml-2 text-base font-medium text-gray-500 dark:text-gray-400">
                — {certName}
              </span>
            </h1>
            {totalQuestions > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                {/* Questions per screen */}
                <div className="flex items-center gap-1.5">
                  <label htmlFor="view-count" className="text-sm text-gray-500 dark:text-gray-400">{t('study.session.perScreen')}</label>
                  <InfoTooltip text={t('study.session.perScreenTooltip')} />
                  <select
                    id="view-count"
                    value={viewCount}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      localStorage.setItem(`cloudmaster_view_count_${certCode}`, String(val));
                      setViewCount(val);
                      setCurrentIdx(0);
                      setAnswerState({});
                      scrollToQuestionsTop();
                    }}
                    className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-card-dark/80 dark:text-slate-200"
                  >
                    {viewCountOptions.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                {/* Batch size */}
                <div className="flex items-center gap-1.5">
                  <label htmlFor="page-size" className="text-sm text-gray-500 dark:text-gray-400">{t('study.session.batch')}</label>
                  <InfoTooltip text={t('study.session.batchTooltip')} />
                  <select
                    id="page-size"
                    value={pageSize}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      localStorage.setItem(`cloudmaster_page_size_${certCode}`, String(val));
                      setPageSize(val);
                      // A screen can't show more questions than the batch holds.
                      if (viewCount > val) {
                        setViewCount(val);
                        localStorage.setItem(`cloudmaster_view_count_${certCode}`, String(val));
                      }
                      const params = new URLSearchParams(searchParams);
                      params.set('page', '0');
                      setSearchParams(params);
                      setCurrentIdx(0);
                      setAnswerState({});
                    }}
                    className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-card-dark/80 dark:text-slate-200"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {viewCount > 1
                    ? t('study.session.questionPositionTotal', { position: t('study.session.questionPositionRange', { start: globalIdx, end: globalEnd }), total: totalQuestions })
                    : t('study.session.questionPositionTotal', { position: t('study.session.questionPositionSingle', { n: globalIdx }), total: totalQuestions })}
                </span>

                {/* Print & PDF export + Shuffle */}
                <div className="no-print flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResetCursor}
                    title={t('study.session.shuffleTooltip')}
                  >
                    🔀 {t('study.session.shuffle')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => window.print()}
                    title={t('study.session.printTooltip')}
                  >
                    🖨️ {t('study.session.print')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPdfModal(true)}
                    title={t('study.session.pdfTooltip')}
                  >
                    📄 {t('study.session.pdf')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ── Progress bar ──────────────────────────────────────────── */}
          {totalQuestions > 0 && (
            <div className="mb-5 space-y-1">
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>
                  {t('study.session.progressLabel')} — <span className="font-medium text-blue-600 dark:text-blue-400">{globalIdx - 1}</span> {t('study.session.progressViewedSuffix', { total: totalQuestions })}
                  {sessionRevealed > 0 && <span className="ml-2 text-green-600 dark:text-green-400">{t('study.session.progressAnsweredSession', { count: sessionRevealed })}</span>}
                </span>
                <span>{Math.round(((globalIdx - 1) / totalQuestions) * 100)}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                <div
                  className="h-full rounded-full bg-blue-600 transition-all duration-500 dark:bg-blue-500"
                  style={{ width: `${Math.min(100, ((globalIdx - 1) / totalQuestions) * 100)}%` }}
                />
              </div>
            </div>
          )}

          </div>
          {/* ── end sticky study header ─────────────────────────────── */}

          {/* ── Daily limit banner (free users) ─────────────────────── */}
          <DailyLimitBanner />

          {/* ── Error / Loading / Empty ───────────────────────────────── */}
          {questionsError && (
            (() => {
              const axErr = questionsError as { response?: { status?: number; data?: { detail?: { code?: string } } } };
              const errorCode = axErr.response?.status === 403 ? axErr.response?.data?.detail?.code : undefined;
              if (errorCode === 'PREMIUM_REQUIRED') {
                return (
                  <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6 text-center">
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{t('study.session.premiumCertTitle')}</p>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {t('study.session.premiumCertDescription')}
                    </p>
                    <Link
                      to="/pricing"
                      className="mt-4 inline-block rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-colors"
                    >
                      {t('study.session.upgradeToPremium')}
                    </Link>
                  </div>
                );
              }
              return (
                <div className="rounded-lg border border-error/30 bg-red-50 p-4 text-sm text-error dark:bg-red-900/20">
                  {t('study.session.errorLoadQuestions')}
                </div>
              );
            })()
          )}
          {questionsLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="spinner h-8 w-8 rounded-full border-4 border-primary border-t-transparent" />
            </div>
          )}
          {!questionsLoading && !questionsError && questions.length === 0 && (
            <Card className="py-16 text-center">
              <p className="text-lg text-slate-500 dark:text-slate-400">
                {topicFilter || difficultyFilter
                  ? t('study.session.emptyNoMatch')
                  : t('study.session.emptyNoMaterials', { certName })}
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {topicFilter || difficultyFilter
                  ? t('study.session.emptyAdjustFilters')
                  : t('study.session.emptyComingSoon')}
              </p>
            </Card>
          )}

          {/* ── Question cards ───────────────────────────────────────── */}
          <div className="relative">
            <div className={`space-y-6 transition-all duration-300 ${isLimitReached ? 'blur-md pointer-events-none select-none' : ''}`}>
              {visibleQuestions.map((q, i) => {
                const state = answerState[q.id] ?? { selected: null, revealed: false, hintRevealed: false };
                // Merge correct_answer and explanation from submit response into question
                const enrichedQ = (state.correct_answer || state.explanation)
                  ? { ...q, correct_answer: state.correct_answer ?? q.correct_answer, explanation: state.explanation ?? q.explanation }
                  : q;
                return (
                  <QuestionCard
                    key={q.id}
                    question={enrichedQ}
                    showAnswer={state.revealed}
                    selectedAnswer={state.selected}
                    slotSelections={state.slotSelections}
                    onSelectOption={(letter) => handleSelectOption(q.id, q.question_type, letter)}
                    onSlotSelect={(slotId, value) => handleSlotSelect(q.id, slotId, value)}
                    onReveal={() => handleReveal(q.id, q)}
                    hintRevealed={state.hintRevealed}
                    onHint={() => handleHint(q.id)}
                    questionNumber={globalIdx + i}
                    totalQuestions={totalQuestions}
                    isFlagged={flagged.has(q.id)}
                    onToggleFlag={() => handleToggleFlag(q.id)}
                  />
                );
              })}
            </div>

            {/* ── Daily limit overlay ────────────────────────────────── */}
            {isLimitReached && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-white/95 dark:bg-gray-900/95 shadow-2xl p-8 text-center max-w-md mx-4">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
                    <svg className="h-7 w-7 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('study.session.dailyLimitTitle')}</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {t('study.session.dailyLimitDescription', { limit: dailyUsage?.limit ?? 20 })}
                  </p>
                  <Link
                    to="/pricing"
                    className="mt-5 inline-block rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white hover:bg-primary/90 transition-colors shadow-lg"
                  >
                    {t('study.session.upgradeToPremium')}
                  </Link>
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {t('study.session.dailyLimitReset')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Navigation ───────────────────────────────────────────── */}
          {questions.length > 0 && (
            <div className="mt-6 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrev}
                disabled={pageParam === 0 && currentIdx === 0}
              >
                ← {t('study.session.previous')}
              </Button>

              <div className="hidden items-center gap-2 sm:flex">
                <label htmlFor="goto-q" className="text-sm text-slate-500 dark:text-slate-400">{t('study.session.goTo')}</label>
                <input
                  id="goto-q"
                  type="number"
                  min={1}
                  max={totalQuestions}
                  className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1 text-center text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 dark:border-white/10 dark:bg-card-dark/80 dark:text-slate-200"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = Number((e.target as HTMLInputElement).value);
                      if (val >= 1 && val <= totalQuestions) {
                        const newPage = Math.floor((val - 1) / pageSize);
                        const newIdx = (val - 1) % pageSize;
                        const params = new URLSearchParams(searchParams);
                        params.set('page', String(newPage));
                        setSearchParams(params);
                        setCurrentIdx(newIdx);
                        setAnswerState({});
                        scrollToQuestionsTop();
                      }
                    }
                  }}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleNext}
                  disabled={globalEnd >= totalQuestions}
                >
                  {t('study.session.next')} →
                </Button>
                {sessionId && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(t('study.session.finishSessionConfirm'))) {
                        completeSession(sessionId).then(() => {
                          window.location.href = '/study';
                        }).catch(() => {});
                      }
                    }}
                    className="rounded-lg border border-green-300 px-3 py-2 text-sm font-medium text-green-600 transition-colors hover:bg-green-50 dark:border-green-500/30 dark:text-green-400 dark:hover:bg-green-500/10"
                  >
                    {t('study.session.finishSession')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ad — minimal placement before footer */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <InlineAd slot="XXXXXXXXXX" />
      </div>
    </AppShell>
  );
}
